/*
 * DRAEN mobile sticky add-to-bag.
 * Shows once the real add-to-bag button leaves the viewport, and mirrors that button's
 * label + disabled state and the price element's text, so variant switches and sold-out
 * states never drift from the in-page controls. Desktop is excluded in CSS.
 */
class DraenStickyAtc extends HTMLElement {
  connectedCallback() {
    this.sourceButton = document.getElementById(this.dataset.sourceButton);
    this.target = document.querySelector(this.dataset.observe);
    if (!this.sourceButton || !this.target) return;

    this.submit = this.querySelector('[data-sticky-submit]');
    this.label = this.querySelector('[data-sticky-label]');
    this.price = this.querySelector('[data-sticky-price]');

    // Visibility: the bar appears only once the real CTA has been scrolled *past* — never
    // on load, when the CTA merely sits below the fold, because 09-ui-mobile-rules.md
    // rules out a sticky CTA that jumps in as the page renders.
    //
    // Measured on scroll rather than with an IntersectionObserver: the observer reports
    // changes in intersection, and a jump — an anchor link, a restored scroll position,
    // scrollTo — goes from "below the fold" to "above the viewport" without ever
    // reporting a change, leaving the bar stuck hidden.
    this.onScroll = () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.setVisible(this.target.getBoundingClientRect().bottom < 0);
      });
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.onScroll();

    // State mirror: Dawn re-renders these nodes on variant change.
    this.stateObserver = new MutationObserver(() => this.sync());
    this.stateObserver.observe(document.getElementById(this.dataset.sourceRoot) || document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['disabled'],
    });
    this.sync();
  }

  disconnectedCallback() {
    if (this.onScroll) {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
    }
    if (this.frame) cancelAnimationFrame(this.frame);
    this.stateObserver?.disconnect();
  }

  setVisible(visible) {
    this.dataset.visible = String(visible);
    // `inert` keeps the hidden bar out of the tab order and off the a11y tree.
    this.toggleAttribute('inert', !visible);
    document.body.style.setProperty(
      '--draen-sticky-atc-height',
      visible ? `${this.offsetHeight}px` : '0px'
    );
  }

  sync() {
    const source = document.getElementById(this.dataset.sourceButton);
    if (!source || !this.submit) return;
    this.submit.disabled = source.disabled;

    // Only borrow the source label when the button is unavailable — that is the state the
    // bar must not misreport. While purchasable it keeps its own short wording, so the
    // compact bar does not inherit "Pre-order now — £69" and blow out on a 375px phone.
    if (this.label) {
      if (source.disabled) {
        const sourceLabel = source.querySelector('span')?.textContent.trim();
        if (sourceLabel) this.label.textContent = sourceLabel;
      } else if (this.dataset.label) {
        this.label.textContent = this.dataset.label;
      }
    }

    const sourcePrice = document
      .getElementById(this.dataset.sourcePrice)
      ?.querySelector('.price-item--sale, .price-item--regular');
    // Mirrors whatever Dawn renders, including the shop's currency format — the bar and
    // the in-page price must never disagree.
    if (sourcePrice && this.price) this.price.textContent = sourcePrice.textContent.trim();
  }
}

customElements.define('draen-sticky-atc', DraenStickyAtc);
